import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Alert, Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faCalendar, faUser, faShippingFast, faClipboardList, faTimes, faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/InfiniteTable.css';

function ExternalOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'created_date', direction: 'desc' });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const { user } = useAuth();

  useEffect(() => {
    fetchOrders();
  }, [pagination.page, statusFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter
      });

      const response = await fetch(`${API_URL}/api/external-orders/my-orders?${queryParams}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      let ordersData = data.orders || [];
      
      // Apply sorting
      if (sortConfig.key) {
        ordersData = [...ordersData].sort((a, b) => {
          let aValue = a[sortConfig.key];
          let bValue = b[sortConfig.key];
          
          // Handle dates
          if (['created_date', 'approved_date'].includes(sortConfig.key)) {
            aValue = new Date(aValue || 0);
            bValue = new Date(bValue || 0);
          }
          
          // Handle numbers
          if (['item_count', 'total_requested_qty'].includes(sortConfig.key)) {
            aValue = Number(aValue) || 0;
            bValue = Number(bValue) || 0;
          }
          
          if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        });
      }
      
      setOrders(ordersData);
      setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load orders. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    fetchOrders(); // Refetch with new sort
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return faSort;
    }
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      const response = await fetch(`${API_URL}/api/external-orders/order/${orderId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSelectedOrder(data);
      setShowOrderModal(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setError('Failed to load order details.');
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/external-orders/order/${orderId}/cancel`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Refresh orders list
      fetchOrders();
    } catch (error) {
      console.error('Error cancelling order:', error);
      setError('Failed to cancel order.');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', text: 'Pending Review' },
      under_review: { variant: 'info', text: 'Under Review' },
      approved: { variant: 'success', text: 'Approved' },
      rejected: { variant: 'danger', text: 'Rejected' },
      processing: { variant: 'primary', text: 'Processing' },
      shipped: { variant: 'success', text: 'Shipped' },
      delivered: { variant: 'dark', text: 'Delivered' },
      cancelled: { variant: 'secondary', text: 'Cancelled' }
    };

    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  if (loading && orders.length === 0) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status" size="lg">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading your orders...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <FontAwesomeIcon icon={faClipboardList} className="me-2" />
                  My Orders
                </h4>
                <div className="d-flex align-items-center">
                  <Form.Select
                    size="sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ maxWidth: '150px' }}
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="under_review">Under Review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                  </Form.Select>
                </div>
              </div>
            </Card.Header>
            <Card.Body style={{ padding: 0 }}>
              {error && (
                <Alert variant="danger" className="m-3">
                  {error}
                </Alert>
              )}

              {orders.length === 0 && !loading ? (
                <Alert variant="info" className="m-3">
                  No orders found. Start shopping to create your first order!
                </Alert>
              ) : (
                <div className="infinite-table-container" style={{ height: 'calc(100vh - 300px)' }}>
                  <div className="table-info">
                    Showing {orders.length} order{orders.length !== 1 ? 's' : ''}
                  </div>
                  <div className="infinite-table-scroll">
                    <Table className="infinite-table">
                      <thead>
                        <tr>
                          <th 
                            className={`sortable-header ${sortConfig.key === 'order_number' ? 'active' : ''}`}
                            onClick={() => handleSort('order_number')}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span>Order Number</span>
                              <FontAwesomeIcon icon={getSortIcon('order_number')} className="sort-indicator" />
                            </div>
                          </th>
                          <th 
                            className={`sortable-header ${sortConfig.key === 'created_date' ? 'active' : ''}`}
                            onClick={() => handleSort('created_date')}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span>Date</span>
                              <FontAwesomeIcon icon={getSortIcon('created_date')} className="sort-indicator" />
                            </div>
                          </th>
                          <th 
                            className={`sortable-header ${sortConfig.key === 'status' ? 'active' : ''}`}
                            onClick={() => handleSort('status')}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span>Status</span>
                              <FontAwesomeIcon icon={getSortIcon('status')} className="sort-indicator" />
                            </div>
                          </th>
                          <th 
                            className={`sortable-header ${sortConfig.key === 'item_count' ? 'active' : ''}`}
                            onClick={() => handleSort('item_count')}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span>Items</span>
                              <FontAwesomeIcon icon={getSortIcon('item_count')} className="sort-indicator" />
                            </div>
                          </th>
                          <th 
                            className={`sortable-header ${sortConfig.key === 'shipping_name' ? 'active' : ''}`}
                            onClick={() => handleSort('shipping_name')}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span>Shipping To</span>
                              <FontAwesomeIcon icon={getSortIcon('shipping_name')} className="sort-indicator" />
                            </div>
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <tr key={order.id}>
                            <td>
                              <strong>{order.order_number}</strong>
                              {order.customer_reference && (
                                <>
                                  <br />
                                  <small className="text-muted">Ref: {order.customer_reference}</small>
                                </>
                              )}
                            </td>
                            <td>
                              <FontAwesomeIcon icon={faCalendar} className="me-1 text-muted" />
                              {formatDate(order.created_date)}
                            </td>
                            <td>{getStatusBadge(order.status)}</td>
                            <td>
                              <strong>{order.item_count}</strong> items
                              <br />
                              <small className="text-muted">{order.total_requested_qty} total qty</small>
                            </td>
                            <td>
                              <FontAwesomeIcon icon={faUser} className="me-1 text-muted" />
                              {order.shipping_name}
                              <br />
                              <small className="text-muted">
                                {order.shipping_city}, {order.shipping_state}
                              </small>
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="table-action-btn me-2"
                                onClick={() => fetchOrderDetails(order.id)}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </Button>
                              {order.status === 'pending' && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="table-action-btn"
                                  onClick={() => cancelOrder(order.id)}
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {loading && (
                      <div className="table-loading">
                        <Spinner animation="border" className="me-2" />
                        Loading orders...
                      </div>
                    )}
                  </div>
                  
                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="table-pagination">
                      <nav>
                        <ul className="pagination mb-0">
                          <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pagination.page - 1)}
                              disabled={pagination.page === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {[...Array(pagination.totalPages)].map((_, index) => (
                            <li
                              key={index + 1}
                              className={`page-item ${pagination.page === index + 1 ? 'active' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(index + 1)}
                              >
                                {index + 1}
                              </button>
                            </li>
                          ))}
                          <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pagination.page + 1)}
                              disabled={pagination.page === pagination.totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Order Details Modal */}
      <Modal show={showOrderModal} onHide={() => setShowOrderModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Order Details - {selectedOrder?.order_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">Order Information</h6>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Status:</strong> {getStatusBadge(selectedOrder.status)}</p>
                    <p><strong>Created:</strong> {formatDate(selectedOrder.created_date)}</p>
                    {selectedOrder.customer_reference && (
                      <p><strong>Reference:</strong> {selectedOrder.customer_reference}</p>
                    )}
                    {selectedOrder.approved_date && (
                      <p><strong>Approved:</strong> {formatDate(selectedOrder.approved_date)}</p>
                    )}
                    {selectedOrder.approved_by_name && (
                      <p><strong>Approved By:</strong> {selectedOrder.approved_by_name}</p>
                    )}
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">
                      <FontAwesomeIcon icon={faUser} className="me-2" />
                      End User
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Name:</strong> {selectedOrder.end_user_name}</p>
                    <p><strong>Email:</strong> {selectedOrder.end_user_email}</p>
                    {selectedOrder.end_user_phone && (
                      <p><strong>Phone:</strong> {selectedOrder.end_user_phone}</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">
                      <FontAwesomeIcon icon={faShippingFast} className="me-2" />
                      Shipping Information
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Recipient:</strong> {selectedOrder.shipping_name}</p>
                    {selectedOrder.shipping_company && (
                      <p><strong>Company:</strong> {selectedOrder.shipping_company}</p>
                    )}
                    <p><strong>Address:</strong><br />
                      {selectedOrder.shipping_address}<br />
                      {selectedOrder.shipping_city}, {selectedOrder.shipping_state} {selectedOrder.shipping_zip}
                    </p>
                    {selectedOrder.shipping_phone && (
                      <p><strong>Phone:</strong> {selectedOrder.shipping_phone}</p>
                    )}
                  </Card.Body>
                </Card>

                {selectedOrder.special_instructions && (
                  <Card className="mb-3">
                    <Card.Header>
                      <h6 className="mb-0">Special Instructions</h6>
                    </Card.Header>
                    <Card.Body>
                      <p>{selectedOrder.special_instructions}</p>
                    </Card.Body>
                  </Card>
                )}
              </Col>

              <Col xs={12}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Order Items</h6>
                  </Card.Header>
                  <Card.Body style={{ padding: 0 }}>
                    <div className="infinite-table-container" style={{ height: '300px' }}>
                      <div className="infinite-table-scroll">
                        <Table className="infinite-table" size="sm">
                          <thead>
                            <tr>
                              <th>Item Number</th>
                              <th>Item Name</th>
                              <th>Requested Qty</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedOrder.items?.map((item, index) => (
                              <tr key={index}>
                                <td><strong>{item.item_number}</strong></td>
                                <td>{item.item_name}</td>
                                <td>{item.requested_quantity}</td>
                                <td>{getStatusBadge(item.item_status)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        {(!selectedOrder.items || selectedOrder.items.length === 0) && (
                          <div className="table-empty">
                            No items found for this order.
                          </div>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOrderModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ExternalOrders; 